import React, { useState, useEffect } from "react";
import "./addProperty.css";
import axios from "axios";
import toast from "react-hot-toast";
import { uploadAndCreateImageEntry } from "../../../../../utils/createImgforLots";
import { uploadAndCreateDocumentEntry } from "../../../../../utils/createPdfforLot";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../../../../redux/actions/modalActions";
import { fetchLots } from "../../../../../redux/actions/immobilierActions";
import mainAxios from "../../../../../redux/api/mainAxios";
import debounce from "../../../../../utils/debounce";

const AjouterLot = () => {
  const dispatch = useDispatch();
  const [orientations, setOrientations] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedImageEntries, setUploadedImageEntries] = useState([]);
  const [uploadedDocumentEntries, setUploadedDocumentEntries] = useState([]);
  const [programmesParent, setProgrammesParent] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([
    { label: "règlement", value: "règlement" },
    { label: "plan de masse", value: "plan_masse" },
    { label: "plan de vente", value: "plan_vente" },
    { label: "plan etage", value: "plan_etage" },
    { label: "plaquette commerciale", value: "plaquette_commerciale" },
    { label: "contrat de réservation", value: "contrat_reservation" },
    { label: "autre", value: "other_documents" },
  ]);
  const [formData, setFormData] = useState({
    reference: null,
    programme_parent: null,
    typology: null,
    surface: null,
    price_total: null,
    rent: null,
    rent_profitability: null,
    weighted_surface: null,
    commission: null,
    price_vat_rate: null,
    type: null,
    parking_price_incl_tax: null,
    price_excl_vat: null,
    parking_price_excl_vat: null,
    price_incl_tax: null,
    parking_price_vat_rate: null,
    parking_count: null,
    is_new: false,
    global_score: null,
    availability: null,
    floor: null,
    orientations: [],
    lastSync: null,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  // Handle document selection
  const handleDocumentSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedDocuments((prev) => [
      ...prev,
      ...files.map((file) => ({
        file,
        type: "autre", // Default type
      })),
    ]);
  };
  // Remove document from selection
  const removeDocument = (index) => {
    setSelectedDocuments((prev) => prev.filter((_, i) => i !== index));
  };
  // Update document type
  const updateDocumentType = (index, type) => {
    setSelectedDocuments((prev) =>
      prev.map((doc, i) => (i === index ? { ...doc, type } : doc))
    );
  };
  const handleChange = (e) => {
    const { name, value, type, checked, multiple, options } = e.target;

    // Handle checkbox inputs
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    }
    // Handle multi-select inputs (for orientations)
    else if (multiple && type === "select-multiple") {
      const selectedValues = Array.from(options)
        .filter((option) => option.selected)
        .map((option) => option.value);
      setFormData((prev) => ({
        ...prev,
        [name]: selectedValues,
      }));
    } else if (name === "programme_parent") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      debouncedSearch(value);
    }
    // Handle all other input types (text, number, single select, etc.)
    else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages((prev) => [...prev, ...files]);
  };

  // Remove image from selection
  const removeImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const fetchOrientations = async () => {
      try {
        const response = await mainAxios.get(`api/orientations`);

        setOrientations(response.data.data);
      } catch (error) {
        console.error("Error fetching orientations:", error);
      }
    };

    fetchOrientations();
  }, []);

  const handleImageUpload = async () => {
    setUploading(true);
    const uploadedEntries = [];

    try {
      // Process each selected image
      for (const file of selectedImages) {
        const reference = formData.reference || `temp-${Date.now()}`;
        const entry = await uploadAndCreateImageEntry(file, reference);
        if (entry) {
          uploadedEntries.push(entry);
        }
      }

      setUploadedImageEntries(uploadedEntries);
      toast.success(
        `${uploadedEntries.length} image(s) téléchargé(s) avec succès`
      );
      return uploadedEntries;
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload one or more images");
      return [];
    } finally {
      setUploading(false);
    }
  };
  const handleDocumentUpload = async () => {
    setUploading(true);
    const uploadedEntries = [];

    try {
      // Process each selected document
      for (const doc of selectedDocuments) {
        const reference = formData.reference || `temp-${Date.now()}`;
        const entry = await uploadAndCreateDocumentEntry(
          doc.file,
          reference,
          doc.type
        );
        if (entry) {
          uploadedEntries.push(entry);
        }
      }

      setUploadedDocumentEntries(uploadedEntries);
      toast.success(
        `${uploadedEntries.length} document(s) téléchargé(s) avec succès`
      );
      return uploadedEntries;
    } catch (error) {
      console.error("Error uploading documents:", error);
      toast.error("Failed to upload one or more documents");
      return [];
    } finally {
      setUploading(false);
    }
  };

  // Submit form with images and documents already uploaded
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setUploading(true);

      // Upload images if they haven't been uploaded yet
      let imageEntries = uploadedImageEntries;
      if (selectedImages.length > 0 && uploadedImageEntries.length === 0) {
        imageEntries = await handleImageUpload();
      }

      // Upload documents if they haven't been uploaded yet
      let documentEntries = uploadedDocumentEntries;
      if (
        selectedDocuments.length > 0 &&
        uploadedDocumentEntries.length === 0
      ) {
        documentEntries = await handleDocumentUpload();
      }

      // Prepare the property data with image and document links
      const dataToSend = {
        ...formData,
        lastSync: new Date().toISOString(),
        // Add the image IDs to connect with the property
        imageIds: imageEntries.map((entry) => entry.id),
        // Add the document IDs to connect with the property
        documentIds: documentEntries.map((entry) => entry.id),
      };

      const response = await mainAxios.post(`api/add-property`, dataToSend);

      toast.success("Lot ajouté avec succès");

      // Reset form after successful submission
      setFormData({
        programme_parent: null,
        reference: null,
        typology: null,
        surface: null,
        price_total: null,
        rent: null,
        rent_profitability: null,
        weighted_surface: null,
        commission: null,
        price_vat_rate: null,
        type: null,
        parking_price_incl_tax: null,
        price_excl_vat: null,
        parking_price_excl_vat: null,
        price_incl_tax: null,
        parking_price_vat_rate: null,
        parking_count: null,
        is_new: false,
        global_score: null,
        availability: null,
        floor: null,
        orientations: [],
        lastSync: null,
      });
      setSelectedImages([]);
      setUploadedImageEntries([]);
      setSelectedDocuments([]);
      setUploadedDocumentEntries([]);
    } catch (error) {
      console.error("Erreur lors de l'ajout du lot:", error);
      toast.error("Erreur lors de l'ajout du lot");
    } finally {
      setUploading(false);
      dispatch(fetchLots());
      dispatch(closeModal());
    }
  };
  const handleProgramsearch = async (value) => {
    try {
      const response = await mainAxios.get(
        `api/programmes?filters[$and][0][name][$containsi]=${value}`
      );
      setProgrammesParent(response.data.data);
    } catch (error) {
      console.error("Erreur lors de la recherche des programmes:", error);
    }
  };

  const debouncedSearch = debounce(handleProgramsearch, 500);
  return (
    <div className="form-container">
      <form onSubmit={handleSubmit}>
        <div className="d-flex gap10">
          <div className="w60">
            <div className="form-row">
              <div className="form-group">
                <label>Programme</label>
                <div className="search-dropdown-container">
                  <input
                    type="text"
                    name="programme_search"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowDropdown(true);
                      debouncedSearch(e.target.value);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Rechercher un programme..."
                    className="search-input"
                  />
                  {showDropdown && programmesParent.length > 0 && (
                    <div className="search-results-dropdown">
                      {programmesParent.map((programme) => (
                        <div
                          key={programme.id}
                          className="search-result-item"
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              programme_parent: programme.documentId,
                            }));
                            setSearchTerm(programme.name);
                            setShowDropdown(false);
                          }}
                        >
                          {programme.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Référence*</label>
                <input
                  type="text"
                  name="reference"
                  value={formData.reference || ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Typologie</label>
                <input
                  type="text"
                  name="typology"
                  value={formData.typology || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Surface (m²)</label>
                <input
                  type="number"
                  name="surface"
                  value={formData.surface || ""}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Prix total (€)</label>
                <input
                  type="number"
                  name="price_total"
                  value={formData.price_total || ""}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Loyer mensuel (€)</label>
                <input
                  type="number"
                  name="rent"
                  value={formData.rent || ""}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Rentabilité loyer (%)</label>
                <input
                  type="number"
                  name="rent_profitability"
                  value={formData.rent_profitability || ""}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  max="100"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Surface pondérée (m²)</label>
                <input
                  type="number"
                  name="weighted_surface"
                  value={formData.weighted_surface || ""}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Commission</label>
                <input
                  type="text"
                  name="commission"
                  value={formData.commission || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Taux TVA prix (%)</label>
                <input
                  type="number"
                  name="price_vat_rate"
                  value={formData.price_vat_rate || ""}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                  max="100"
                />
              </div>
              <div className="form-group">
                <label>Type de lot</label>
                <select
                  name="type"
                  value={formData.type || ""}
                  onChange={handleChange}
                >
                  <option value="">--Sélectionner--</option>
                  <option value="appartement">Appartement</option>
                  <option value="maison">Maison</option>
                  <option value="parking">Parking</option>
                  <option value="commerce">Commerce</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Prix parking TTC (€)</label>
                <input
                  type="number"
                  name="parking_price_incl_tax"
                  value={formData.parking_price_incl_tax || ""}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Prix HT (€)</label>
                <input
                  type="number"
                  name="price_excl_vat"
                  value={formData.price_excl_vat || ""}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Prix parking HT (€)</label>
                <input
                  type="number"
                  name="parking_price_excl_vat"
                  value={formData.parking_price_excl_vat || ""}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Prix TTC (€)</label>
                <input
                  type="number"
                  name="price_incl_tax"
                  value={formData.price_incl_tax || ""}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Taux TVA parking (%)</label>
                <input
                  type="number"
                  name="parking_price_vat_rate"
                  value={formData.parking_price_vat_rate || ""}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                  max="100"
                />
              </div>
              <div className="form-group">
                <label>Nombre de places parking</label>
                <input
                  type="number"
                  name="parking_count"
                  value={formData.parking_count || ""}
                  onChange={handleChange}
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group ">
                <label className="d-flex gap-2">
                  <input
                    type="checkbox"
                    name="is_new"
                    checked={formData.is_new}
                    onChange={handleChange}
                  />
                  Lot neuf
                </label>
              </div>
              <div className="form-group">
                <label>Score global</label>
                <input
                  type="number"
                  name="global_score"
                  value={formData.global_score || ""}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                  max="20"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Disponibilité</label>
                <select
                  name="availability"
                  value={formData.availability || ""}
                  onChange={handleChange}
                >
                  <option value="">--Sélectionnez--</option>
                  <option value="disponible">Disponible</option>
                  <option value="optionné">Optionné</option>
                </select>
              </div>

              <div className="form-group">
                <label>Étage</label>
                <input
                  type="text"
                  name="floor"
                  value={formData.floor || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Orientations</label>
              <select
                name="orientations"
                multiple
                value={formData.orientations}
                onChange={handleChange}
                className="form-control"
              >
                {orientations.map((orientation) => (
                  <option
                    key={orientation.documentId}
                    value={orientation.documentId}
                  >
                    {orientation.orientation}
                  </option>
                ))}
              </select>
              <small className="text-muted">
                Maintenez Ctrl (Windows) ou Command (Mac) pour sélectionner
                plusieurs options
              </small>
            </div>
          </div>

          <div className="w40">
            {/* Images Section */}
            <div className="form-group d-flex flex-column">
              <label>Images</label>

              {/* Custom French button that triggers the hidden input */}
              <button
                type="button"
                className=" radius5 px-20 py-10 bg-pink text-white border-0 hoverOpacity"
                onClick={() => document.getElementById("image-input").click()}
              >
                Sélectionner des images
              </button>

              {/* Hidden actual file input */}
              <input
                id="image-input"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="d-none"
              />

              {selectedImages.length > 0 && (
                <div className="selected-images d-flex flex-column gap10 mt-2">
                  <p className="fw-bold mb-2">
                    Images sélectionnées ({selectedImages.length}):
                  </p>
                  <div className="image-previews d-flex flex-wrap gap10">
                    {selectedImages.map((file, index) => (
                      <div
                        key={index}
                        className="image-preview-item position-relative p-2 border rounded bg-light"
                      >
                        <p className="lot-image-name mb-0">{file.name}</p>
                        <button
                          type="button"
                          className="remove-image-btn position-absolute top-0 end-0 btn btn-sm text-danger"
                          onClick={() => removeImage(index)}
                          aria-label="Supprimer cette image"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>

                  {uploadedImageEntries.length > 0 && (
                    <div className="upload-success mt-2">
                      <p className="text-success">
                        {uploadedImageEntries.length} image(s) téléchargée(s)
                        avec succès
                      </p>
                    </div>
                  )}

                  {!formData.reference && selectedImages.length > 0 && (
                    <p className="text-warning mt-2">
                      Veuillez remplir le champ "Référence" avant de télécharger
                      les images
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Documents Section */}
            <div className="form-group d-flex flex-column mt-4">
              <label>Documents PDF</label>

              {/* Custom French button that triggers the hidden input */}
              <button
                type="button"
                className="radius5 px-20 py-10 bg-pink text-white border-0 hoverOpacity"
                onClick={() =>
                  document.getElementById("document-input").click()
                }
              >
                Sélectionner des documents PDF
              </button>

              {/* Hidden actual file input */}
              <input
                id="document-input"
                type="file"
                accept=".pdf"
                multiple
                onChange={handleDocumentSelect}
                className="d-none"
              />

              {selectedDocuments.length > 0 && (
                <div className="selected-documents d-flex flex-column gap10 mt-2">
                  <p className="fw-bold mb-2">
                    Documents sélectionnés ({selectedDocuments.length}):
                  </p>
                  <div className="document-previews d-flex flex-wrap gap10">
                    {selectedDocuments.map((doc, index) => (
                      <div
                        key={index}
                        className="document-preview-item position-relative p-2 border rounded bg-light"
                      >
                        <p className="lot-document-name mb-2">
                          {doc.file.name}
                        </p>
                        <div className="document-type-selector mb-1">
                          <select
                            value={doc.type}
                            onChange={(e) =>
                              updateDocumentType(index, e.target.value)
                            }
                            className="form-select form-select-sm"
                          >
                            {documentTypes.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          className="remove-image-btn position-absolute top-0 end-0 btn btn-sm text-danger"
                          onClick={() => removeDocument(index)}
                          aria-label="Supprimer ce document"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>

                  {uploadedDocumentEntries.length > 0 && (
                    <div className="upload-success mt-2">
                      <p className="text-success">
                        {uploadedDocumentEntries.length} document(s)
                        téléchargé(s) avec succès
                      </p>
                    </div>
                  )}

                  {!formData.reference && selectedDocuments.length > 0 && (
                    <p className="text-warning mt-2">
                      Veuillez remplir le champ "Référence" avant de télécharger
                      les documents
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={uploading}
          className={`radius5 px-20 py-10 text-white border-0 hoverOpacity ${
            uploading ? "bg-soft-grey" : "bg-pink"
          }`}
        >
          {uploading ? "En cours..." : "Enregistrer le lot"}
        </button>
      </form>
    </div>
  );
};

export default AjouterLot;
